<template>
  <div id="worktops-settings">
    <div id="view-sub-nav">
      <v-btn
        variant="outlined"
        color="primary"
        @click="
          () => {
            selectedWorktop = null
            resetWorktopForm()
            worktopDialog = true
          }
        "
        >Create New Worktop</v-btn
      >
      <v-spacer />
      <v-switch
        v-if="$store.state.user.permissions.admin"
        v-model="showArchived"
        label="Show archived"
        hide-details
        density="compact"
        class="mr-4"
        @update:model-value="onShowArchivedChange"
      ></v-switch>
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        density="compact"
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      v-if="worktops"
      :items="tableItems"
      :loading="loading || loadingArchived"
      :headers="tableHeaders"
      :sort-by="showArchived ? [{ key: 'dateArchived', order: 'desc' }] : [{ key: 'text', order: 'asc' }]"
      :group-by="showArchived ? [{ key: 'value', order: 'asc' }] : []"
      :items-per-page="25"
      :search="tableSearch"
      id="worktops-table"
    >
      <template v-slot:group-header="{ item, columns, isGroupOpen, toggleGroup }">
        <tr>
          <td :colspan="columns.length" class="bg-grey-lighten-4" style="cursor: pointer" @click="toggleGroup(item)">
            <v-icon size="small" class="mr-2">{{
              isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right'
            }}</v-icon>
            <strong>{{ (item.items[0] && item.items[0].raw.text) || item.value }}</strong>
            <span class="text-caption text-grey-darken-1 ml-2">{{ item.value }}</span>
            <span class="text-caption text-grey ml-2"
              >· {{ item.items.length }} archived version{{ item.items.length === 1 ? '' : 's' }}</span
            >
            <v-chip v-if="hasLiveCounterpart(item.value)" size="x-small" color="green-lighten-4" class="ml-3"
              >has live</v-chip
            >
            <v-chip v-else size="x-small" color="orange-lighten-4" class="ml-3">discontinued</v-chip>
          </td>
        </tr>
      </template>
      <template v-slot:item.regions="{ item }">
        {{ (item.regions || (item.region ? [item.region] : [])).map((r) => r.toUpperCase()).join(', ') }}
      </template>
      <template v-slot:item.sizes="{ item }">
        {{ item.sizes ? item.sizes.length : 0 }}
      </template>
      <template v-slot:item.dateArchived="{ item }">
        {{ item.dateArchived ? $moment(item.dateArchived.toDate()).format('Do MMM YYYY') : '—' }}
      </template>
      <template v-slot:item.actions="{ item }">
        <td class="d-flex justify-end align-center">
          <v-tooltip v-if="$store.state.user.permissions.admin && !item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon size="small" class="mr-2" @click="openReplaceDialog(item)" v-bind="props">mdi-pencil</v-icon>
            </template>
            <span>Edit Worktop (archives old, creates new live)</span>
          </v-tooltip>
          <v-tooltip v-if="$store.state.user.permissions.admin && !item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon size="small" class="mr-2" @click="openArchiveOnlyDialog(item)" v-bind="props"
                >mdi-archive-arrow-down</v-icon
              >
            </template>
            <span>Archive Worktop (discontinue, no replacement)</span>
          </v-tooltip>
          <v-tooltip v-if="item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon
                size="small"
                class="mr-2"
                @click="
                  () => {
                    selectedWorktop = item
                    sizesDialog = true
                  }
                "
                v-bind="props"
                >mdi-image-size-select-small</v-icon
              >
            </template>
            <span>View Sizes</span>
          </v-tooltip>
          <v-tooltip
            v-if="$store.state.user.permissions.admin && item.archived && !hasLiveCounterpart(item.value)"
            location="top"
            :open-delay="500"
          >
            <template v-slot:activator="{ props }">
              <v-icon size="small" class="mr-2" @click="openReviveDialog(item)" v-bind="props"
                >mdi-archive-arrow-up</v-icon
              >
            </template>
            <span>Revive Worktop (creates new live)</span>
          </v-tooltip>
        </td>
      </template>
    </v-data-table>

    <!-- CREATE -->
    <v-dialog v-model="worktopDialog" width="800">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Create New Worktop</v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="worktopForm">
            <v-text-field
              label="Code"
              v-model="worktopForm.value"
              :rules="[(v) => !!v || 'Code is required - ask Tim if you do not know what this should be!']"
            ></v-text-field>
            <v-text-field
              label="Name"
              v-model="worktopForm.text"
              :rules="[(v) => !!v || 'Name is required']"
            ></v-text-field>
            <v-select
              v-model="worktopForm.regions"
              :items="Object.values(productData.regions)"
              label="Regions"
              :rules="[(v) => (v && v.length > 0) || 'At least one region required']"
              multiple
              chips
            ></v-select>
            <v-select
              v-if="worktopForm.regions && worktopForm.regions.length > 0"
              v-model="worktopForm.material"
              :items="materialsForRegions(worktopForm.regions)"
              label="Material (colour source)"
              :rules="[(v) => !!v || 'Material required']"
            ></v-select>

            <div class="d-flex align-center mt-4 mb-2">
              <div class="text-subtitle-1">Sheet Sizes</div>
              <v-spacer></v-spacer>
              <v-icon @click="worktopForm.sizes.push({ thickness: null, size: null, sided: null, cost: null })"
                >mdi-plus</v-icon
              >
            </div>
            <v-row v-for="(sheet, key) in worktopForm.sizes" :key="'ns' + key">
              <v-col :cols="3">
                <v-select
                  hide-details
                  v-model.number="sheet.thickness"
                  label="Thickness"
                  :items="thicknesses"
                ></v-select>
              </v-col>
              <v-col :cols="3">
                <v-select hide-details v-model="sheet.size" label="Size" :items="sizes"></v-select>
              </v-col>
              <v-col :cols="3">
                <v-select hide-details v-model="sheet.sided" label="Sided" :items="sides"></v-select>
              </v-col>
              <v-col :cols="3" class="d-flex align-baseline">
                <v-text-field
                  hide-details
                  v-model.number="sheet.cost"
                  label="Cost"
                  :prefix="costPrefixForRegions(worktopForm.regions)"
                ></v-text-field>
                <v-icon class="ml-2" @click="worktopForm.sizes.splice(key, 1)">mdi-delete</v-icon>
              </v-col>
            </v-row>

            <v-text-field
              v-if="formError"
              v-model="formError"
              error
              variant="outlined"
              readonly
              class="mt-4"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="worktopDialog = false" :disabled="submitting">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="createNewWorktop" :disabled="submitting">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT (archive + replace) / REVIVE -->
    <v-dialog v-model="replaceDialog" v-if="replaceDraft" width="900" scrollable>
      <v-card>
        <v-card-title class="bg-primary text-white text-h6 pa-0">
          <div class="px-4 pt-3 pb-1">{{ replaceMode === 'revive' ? 'Revive Worktop' : 'Edit Worktop' }}</div>
          <v-tabs v-model="replaceTab" bg-color="primary" slider-color="accent" class="px-2">
            <v-tab>
              Worktop
              <v-icon v-if="replaceTabErrors.details" size="small" color="error" class="ml-1">mdi-alert-circle</v-icon>
            </v-tab>
            <v-tab>
              Sizes
              <v-icon v-if="replaceTabErrors.sizes" size="small" color="error" class="ml-1">mdi-alert-circle</v-icon>
            </v-tab>
          </v-tabs>
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 80vh">
          <div v-if="replaceMode === 'revive'" class="text-caption mb-3">
            Confirming will create a new live <strong>{{ selectedWorktop && selectedWorktop.text }}</strong> worktop
            with these values. The archived version stays archived as part of the audit trail.
          </div>
          <div v-else class="text-caption mb-3">
            Confirming will archive the current <strong>{{ selectedWorktop && selectedWorktop.text }}</strong> and
            create a new live version with these values. Existing quotes automatically re-price to the new version;
            existing orders stay frozen to what was sold.
          </div>
          <v-form ref="replaceForm">
            <v-tabs-window v-model="replaceTab">
              <!-- Worktop details -->
              <v-tabs-window-item eager>
                <v-text-field label="Code" v-model="replaceDraft.value" disabled></v-text-field>
                <v-text-field
                  label="Name"
                  v-model="replaceDraft.text"
                  :rules="[(v) => !!v || 'Name is required']"
                ></v-text-field>
                <v-select
                  v-model="replaceDraft.regions"
                  :items="Object.values(productData.regions)"
                  label="Regions"
                  :rules="[(v) => (v && v.length > 0) || 'At least one region required']"
                  multiple
                  chips
                ></v-select>
                <v-select
                  v-if="replaceDraft.regions && replaceDraft.regions.length > 0"
                  v-model="replaceDraft.material"
                  :items="materialsForRegions(replaceDraft.regions)"
                  label="Material (colour source)"
                  :rules="[(v) => !!v || 'Material required']"
                ></v-select>
              </v-tabs-window-item>

              <!-- Sizes -->
              <v-tabs-window-item eager>
                <div class="d-flex align-center mb-2">
                  <div class="text-subtitle-1">Sheet Sizes</div>
                  <v-spacer></v-spacer>
                  <v-icon @click="replaceDraft.sizes.push({ thickness: null, size: null, sided: null, cost: null })"
                    >mdi-plus</v-icon
                  >
                </div>
                <div v-if="!replaceDraft.sizes.length" class="text-caption text-grey py-4 text-center">
                  No sheet sizes — click + to add one.
                </div>
                <v-row v-for="(sheet, key) in replaceDraft.sizes" :key="'s' + key">
                  <v-col :cols="3">
                    <v-select
                      hide-details
                      v-model.number="sheet.thickness"
                      label="Thickness"
                      :items="thicknesses"
                    ></v-select>
                  </v-col>
                  <v-col :cols="3">
                    <v-select hide-details v-model="sheet.size" label="Size" :items="sizes"></v-select>
                  </v-col>
                  <v-col :cols="3">
                    <v-select hide-details v-model="sheet.sided" label="Sided" :items="sides"></v-select>
                  </v-col>
                  <v-col :cols="3" class="d-flex align-baseline">
                    <v-text-field
                      hide-details
                      v-model.number="sheet.cost"
                      label="Cost"
                      :prefix="costPrefixForRegions(replaceDraft.regions)"
                    ></v-text-field>
                    <v-icon class="ml-2" @click="replaceDraft.sizes.splice(key, 1)">mdi-delete</v-icon>
                  </v-col>
                </v-row>
              </v-tabs-window-item>
            </v-tabs-window>
          </v-form>
          <v-text-field
            v-if="replaceError"
            v-model="replaceError"
            error
            variant="outlined"
            readonly
            class="mt-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="closeReplaceDialog" :disabled="submitting">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="confirmReplacement" :disabled="submitting || !replaceDirty">{{
            replaceMode === 'revive' ? 'Confirm Revival' : 'Confirm Replacement'
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ARCHIVE ONLY (discontinue, no replacement) -->
    <v-dialog v-model="archiveOnlyDialog" max-width="480" persistent>
      <v-card>
        <div class="px-4 pt-3 pb-1">Archive Worktop</div>
        <v-card-text>
          <div class="text-caption mb-3">
            Confirming will discontinue <strong>{{ archiveTarget && archiveTarget.text }}</strong> with no replacement.
            New quotes won't list it; existing orders stay frozen to what was sold, and quotes referencing it fall back
            to this archived version.
          </div>
          <v-text-field
            v-if="archiveOnlyError"
            v-model="archiveOnlyError"
            error
            variant="outlined"
            readonly
            class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="archiveOnlyDialog = false" :disabled="submitting">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="confirmArchiveOnly" :disabled="submitting">Archive</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sizes (read-only view of an archived worktop) -->
    <v-dialog
      v-model="sizesDialog"
      v-if="selectedWorktop"
      fullscreen
      transition="slide-x-reverse-transition"
      origin="align-center"
      :max-width="300"
      attach="#worktops-table"
    >
      <v-card class="mb-3 elevation-0">
        <v-card-title class="text-subtitle-1">
          <div class="text-subtitle-1">{{ selectedWorktop.text }} Sheet Sizes</div>
          <v-chip size="x-small" color="orange-lighten-4" class="ml-3">Archived</v-chip>
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row v-for="(sheet, key) in selectedWorktop.sizes" :key="key">
            <v-col :cols="3">
              <v-select
                hide-details
                readonly
                v-model.number="sheet.thickness"
                label="Thickness"
                :items="thicknesses"
              ></v-select>
            </v-col>
            <v-col :cols="3">
              <v-select hide-details readonly v-model="sheet.size" label="Size" :items="sizes"></v-select>
            </v-col>
            <v-col :cols="3">
              <v-select hide-details readonly v-model="sheet.sided" label="Sided" :items="sides"></v-select>
            </v-col>
            <v-col :cols="3" class="d-flex align-baseline">
              <v-text-field
                hide-details
                readonly
                v-model.number="sheet.cost"
                label="Cost"
                :prefix="costPrefix(selectedWorktop)"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import productData from '@/js/productData'
import Materials from '@/js/Materials'
import { db } from '@/js/db'
import { addDoc, collection, doc, getDocs, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore'

export default {
  name: 'WorktopSettings',
  data() {
    return {
      Materials,
      loading: true,
      tableSearch: null,
      headers: [
        { title: 'Name', sortable: false, key: 'text' },
        { title: 'Regions', sortable: false, key: 'regions' },
        { title: 'Sizes', sortable: false, key: 'sizes' },
        { title: 'Actions', sortable: false, align: 'end', key: 'actions' }
      ],
      selectedWorktop: null,
      worktops: null,
      worktopDialog: false,
      sizesDialog: false,
      worktopForm: {
        value: null,
        text: null,
        regions: [],
        material: null,
        sizes: []
      },
      formError: null,
      submitting: false,
      // archive + replace / revive
      replaceDialog: false,
      replaceDraft: null,
      replaceMode: 'replace',
      replaceTab: 0,
      replaceError: null,
      replaceOriginalJSON: null,
      // archive only
      archiveOnlyDialog: false,
      archiveTarget: null,
      archiveOnlyError: null,
      // show archived
      showArchived: false,
      archivedWorktopsList: [],
      loadingArchived: false,
      thicknesses: [
        { text: '18mm', value: 18 },
        { text: '24mm', value: 24 },
        { text: '36mm', value: 36 }
      ],
      sizes: [
        { text: 'Regular', value: 'regular' },
        { text: 'Jumbo', value: 'large' }
      ],
      sides: [
        { text: 'Single', value: 'single' },
        { text: 'Double', value: 'double' }
      ],
      productData
    }
  },
  async mounted() {
    // Bind the whole collection; live vs archived is split client-side.
    await this.$live('worktops', collection(db, 'worktops'))
    this.loading = false
  },
  computed: {
    liveWorktops() {
      return (this.worktops || []).filter((w) => !w.archived)
    },
    tableItems() {
      return this.showArchived ? this.archivedWorktopsList : this.liveWorktops
    },
    tableHeaders() {
      if (!this.showArchived) return this.headers
      return [
        { key: 'data-table-group', title: '', width: 0 },
        { title: 'Name', sortable: false, key: 'text' },
        { title: 'Regions', sortable: false, key: 'regions' },
        { title: 'Sizes', sortable: false, key: 'sizes' },
        { title: 'Archived', sortable: true, key: 'dateArchived' },
        { title: 'Actions', sortable: false, align: 'end', key: 'actions' }
      ]
    },
    replaceTabErrors() {
      const d = this.replaceDraft
      if (!d) return { details: false, sizes: false }
      const details = !d.text || !d.regions || !d.regions.length || !d.material
      const sizes = !d.sizes.length || d.sizes.some((s) => s.thickness == null || !s.size || !s.sided || s.cost == null)
      return { details, sizes }
    },
    replaceDirty() {
      if (this.replaceMode === 'revive') return true
      if (!this.replaceDraft) return false
      return JSON.stringify(this.replaceDraft) !== this.replaceOriginalJSON
    }
  },
  methods: {
    costPrefix(worktop) {
      const regions = worktop.regions || (worktop.region ? [worktop.region] : [])
      return regions.includes('us') ? '$' : '£'
    },
    costPrefixForRegions(regions) {
      return (regions || []).includes('us') ? '$' : '£'
    },
    materialsForRegions(regions) {
      const regs = regions || []
      if (regs.length === 0) return []
      const sets = regs.map((r) => Materials.getMaterialsByRegion(r) || [])
      return sets.reduce((acc, list) => acc.filter((m) => list.some((x) => x.value === m.value)), sets[0])
    },
    hasLiveCounterpart(value) {
      return this.liveWorktops.some((w) => w.value === value)
    },
    resetWorktopForm() {
      this.worktopForm = {
        value: null,
        text: null,
        regions: [],
        material: null,
        sizes: []
      }
      this.formError = null
    },
    async createNewWorktop() {
      if (!(await this.$utils.validateForm(this.$refs.worktopForm))) return
      this.submitting = true
      try {
        // Code must not collide with any live or archived worktop.
        const snap = await getDocs(query(collection(db, 'worktops'), where('value', '==', this.worktopForm.value)))
        if (!snap.empty) {
          const hasLive = snap.docs.some((d) => !d.data().archived)
          this.formError = hasLive
            ? `Code '${this.worktopForm.value}' is already in use by a live worktop. Use Edit on the existing row to update it.`
            : `Code '${this.worktopForm.value}' is in use by an archived worktop. Revive it from the Archived list instead.`
          this.submitting = false
          return
        }

        await addDoc(collection(db, 'worktops'), { ...this.worktopForm })
        this.$refs.worktopForm.resetValidation()
        this.resetWorktopForm()
        this.worktopDialog = false
      } catch (err) {
        this.formError = 'Error: ' + (err.message || err)
      } finally {
        this.submitting = false
      }
    },
    buildReplaceDraft(worktop) {
      return {
        value: worktop.value,
        text: worktop.text,
        regions: worktop.regions ? [...worktop.regions] : worktop.region ? [worktop.region] : [],
        material: worktop.material || null,
        sizes: (worktop.sizes || []).map((s) => ({ ...s }))
      }
    },
    openReplaceDialog(worktop) {
      this.selectedWorktop = worktop
      this.replaceMode = 'replace'
      this.replaceDraft = this.buildReplaceDraft(worktop)
      this.replaceOriginalJSON = JSON.stringify(this.replaceDraft)
      this.replaceError = null
      this.replaceTab = 0
      this.replaceDialog = true
    },
    openReviveDialog(worktop) {
      this.selectedWorktop = worktop
      this.replaceMode = 'revive'
      this.replaceDraft = this.buildReplaceDraft(worktop)
      this.replaceOriginalJSON = JSON.stringify(this.replaceDraft)
      this.replaceError = null
      this.replaceTab = 0
      this.replaceDialog = true
    },
    closeReplaceDialog() {
      this.replaceDialog = false
      this.replaceDraft = null
      this.replaceOriginalJSON = null
      this.replaceError = null
      this.replaceMode = 'replace'
    },
    async confirmReplacement() {
      if (!this.replaceDirty) {
        this.replaceError = 'No changes to save.'
        return
      }
      const formValid = await this.$utils.validateForm(this.$refs.replaceForm)
      const errs = this.replaceTabErrors
      if (!formValid || errs.details || errs.sizes) {
        if (errs.details) this.replaceTab = 0
        else if (errs.sizes) this.replaceTab = 1
        this.replaceError = 'Please fix the highlighted fields before confirming.'
        return
      }
      this.replaceError = null
      this.submitting = true
      try {
        const value = this.replaceDraft.value
        const isRevive = this.replaceMode === 'revive'

        // Revive guard: re-check no live counterpart appeared since the dialog opened.
        if (isRevive && this.hasLiveCounterpart(value)) {
          this.replaceError = `A live worktop with code '${value}' already exists. Use Edit on it instead.`
          this.submitting = false
          return
        }

        const replacement = {
          value: this.replaceDraft.value,
          text: this.replaceDraft.text,
          regions: this.replaceDraft.regions,
          material: this.replaceDraft.material,
          sizes: this.replaceDraft.sizes
        }
        const newRef = doc(collection(db, 'worktops'))

        if (isRevive) {
          await setDoc(newRef, replacement)
          this.$store.commit('openSnackbar', { snackbarText: 'Worktop revived — new live version created' })
        } else {
          const prevArchivedDate = await this.findPrevArchivedDate(value)
          const now = this.$utils.tsNow()
          const batch = writeBatch(db)
          const oldRef = doc(db, 'worktops', this.selectedWorktop.id)
          batch.update(oldRef, { archived: true, dateArchived: now, prevArchivedDate: prevArchivedDate })
          batch.set(newRef, replacement)
          await batch.commit()
          this.$store.commit('openSnackbar', { snackbarText: 'Worktop replaced — old version archived' })
        }

        await this.refreshArchivedList()
        this.closeReplaceDialog()
      } catch (err) {
        this.replaceError = 'Error: ' + (err.message || err)
      } finally {
        this.submitting = false
      }
    },
    openArchiveOnlyDialog(worktop) {
      this.archiveTarget = worktop
      this.archiveOnlyError = null
      this.archiveOnlyDialog = true
    },
    async confirmArchiveOnly() {
      if (!this.archiveTarget) return
      this.archiveOnlyError = null
      this.submitting = true
      try {
        const value = this.archiveTarget.value
        const prevArchivedDate = await this.findPrevArchivedDate(value)
        const now = this.$utils.tsNow()
        await updateDoc(doc(db, 'worktops', this.archiveTarget.id), {
          archived: true,
          dateArchived: now,
          prevArchivedDate: prevArchivedDate
        })

        this.$store.commit('openSnackbar', { snackbarText: 'Worktop archived — discontinued with no replacement' })
        await this.refreshArchivedList()
        this.archiveOnlyDialog = false
        this.archiveTarget = null
      } catch (err) {
        this.archiveOnlyError = 'Error: ' + (err.message || err)
      } finally {
        this.submitting = false
      }
    },
    async findPrevArchivedDate(value) {
      const priorSnap = await getDocs(
        query(collection(db, 'worktops'), where('value', '==', value), where('archived', '==', true))
      )
      let prevArchivedDate = null
      priorSnap.forEach((priorDoc) => {
        const d = priorDoc.data().dateArchived
        if (d && (!prevArchivedDate || d.seconds > prevArchivedDate.seconds)) prevArchivedDate = d
      })
      return prevArchivedDate
    },
    async onShowArchivedChange(value) {
      if (!value) return
      if (this.archivedWorktopsList.length) return
      this.loadingArchived = true
      try {
        const snap = await getDocs(query(collection(db, 'worktops'), where('archived', '==', true)))
        this.archivedWorktopsList = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      } catch (err) {
        this.$store.commit('openSnackbar', { snackbarText: 'Error loading archived: ' + err.message })
      } finally {
        this.loadingArchived = false
      }
    },
    async refreshArchivedList() {
      this.archivedWorktopsList = []
      if (this.showArchived) await this.onShowArchivedChange(true)
    }
  }
}
</script>

<style lang="scss" scoped>
#worktops-settings {
  height: 100%;
  display: flex;
  flex-direction: column;
  #worktops-table {
    flex: 1;
    position: relative;
  }
}
</style>
